import { useState } from 'react';
import { CheckCircle2, XCircle, Edit3 } from 'lucide-react';

function ConfidenceBadge({ value }) {
  if (value == null || isNaN(value)) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono border bg-secondary text-muted-foreground border-border">
        —
      </span>
    );
  }
  const cls = value >= 0.9
    ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900'
    : value >= 0.75
    ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900'
    : 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-900';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono border ${cls}`}>
      {Math.round(value * 100)}%
    </span>
  );
}

export default function AssetsPanel({ assets = [], onReview }) {
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const startEdit = (asset) => {
    setEditingId(asset.id);
    setEditValues({ ...asset });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  return (
    <div className="divide-y divide-slate-800/30" data-testid="assets-panel">
      {assets.map((asset) => (
        <div
          key={asset.id}
          className="px-4 py-3 hover:bg-secondary/20 transition-colors group"
          data-testid={`review-asset-${asset.id}`}
        >
          {editingId === asset.id ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Tag</label>
                  <input
                    value={editValues.tag || ''}
                    onChange={(e) => setEditValues({ ...editValues, tag: e.target.value })}
                    className="w-full bg-card border border-primary/50 rounded-sm px-2 py-1 font-mono text-xs text-foreground outline-none"
                  />
                </div>
                <div>
                  <label className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Room</label>
                  <input
                    value={editValues.room_number || ''}
                    onChange={(e) => setEditValues({ ...editValues, room_number: e.target.value })}
                    className="w-full bg-card border border-primary/50 rounded-sm px-2 py-1 font-mono text-xs text-foreground outline-none"
                  />
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => { onReview('asset_candidate', asset.id, asset.status || 'pending', { tag: editValues.tag, room_number: editValues.room_number }); cancelEdit(); }}
                  data-testid={`review-save-asset-${asset.id}`}
                  className="px-2 py-1 text-[10px] font-mono bg-emerald-950/30 text-emerald-400 border border-emerald-900 rounded hover:bg-emerald-950/50 transition-colors">Save</button>
                <button onClick={cancelEdit} className="px-2 py-1 text-[10px] font-mono text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-primary font-medium">{asset.tag}</span>
                  <span className="font-mono text-xs text-muted-foreground">{asset.asset_type}</span>
                </div>
                <ConfidenceBadge value={asset.confidence} />
              </div>
              <div className="flex items-center gap-3 text-[10px] font-mono text-muted-foreground">
                <span>Room: {asset.room_number || 'Unassigned'}</span>
                <span>{asset.extraction_method}</span>
                {asset.manufacturer && <span data-testid={`asset-mfr-${asset.id}`}>MFR: {asset.manufacturer}</span>}
                {asset.model_info && <span data-testid={`asset-model-${asset.id}`}>MODEL: {asset.model_info}</span>}
              </div>
              <div className="flex items-center gap-1 mt-2">
                <button onClick={() => onReview('asset_candidate', asset.id, 'approved')} data-testid={`review-approve-asset-${asset.id}`}
                  className={`px-2 py-0.5 text-[10px] font-mono rounded border flex items-center gap-1 transition-colors ${
                    asset.status === 'approved'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-900 hover:bg-emerald-100 dark:hover:bg-emerald-950/50'
                  }`}>
                  <CheckCircle2 className="w-3 h-3" /> Approve
                </button>
                <button onClick={() => onReview('asset_candidate', asset.id, 'rejected')} data-testid={`review-reject-asset-${asset.id}`}
                  className={`px-2 py-0.5 text-[10px] font-mono rounded border flex items-center gap-1 transition-colors ${
                    asset.status === 'rejected'
                      ? 'bg-rose-600 text-white border-rose-600'
                      : 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400 border-rose-300 dark:border-rose-900 hover:bg-rose-100 dark:hover:bg-rose-950/50'
                  }`}>
                  <XCircle className="w-3 h-3" /> Reject
                </button>
                <button onClick={() => startEdit(asset)} data-testid={`review-edit-asset-${asset.id}`}
                  className="px-2 py-0.5 text-[10px] font-mono text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 border border-transparent">
                  <Edit3 className="w-3 h-3" /> Edit
                </button>
              </div>
            </>
          )}
        </div>
      ))}
      {assets.length === 0 && (
        <div className="py-8 text-center text-muted-foreground text-xs">No assets for this sheet</div>
      )}
    </div>
  );
}
